import { useEffect, useRef, useState } from 'react';
import { api, endpoints } from '../../api';
import { applyRuntimePreferences } from '../settings/runtime-preferences';
import { ApiError, uuid } from '../../api/client';

interface Intent {
  operation_id: string;
  receipt: string;
}
const storage = 'steos:identity:merge';
function pending(): Intent | null {
  try {
    return JSON.parse(sessionStorage.getItem(storage) || 'null');
  } catch {
    return null;
  }
}
/** Квитанция разрешает только статус. Пароль остаётся в памяти формы и не сохраняется. */
export function useIdentityMerge(onChanged: () => Promise<void>) {
  const [intent, setIntent] = useState<Intent | null>(pending),
    [state, setState] = useState(''),
    [error, setError] = useState('');
  const callback = useRef(onChanged);
  useEffect(() => {
    callback.current = onChanged;
  }, [onChanged]);
  useEffect(() => {
    if (!intent) return;
    const controller = new AbortController();
    let timer: number | undefined;
    const poll = async () => {
      try {
        const result = await endpoints.account.mergeStatus(intent.operation_id, intent.receipt, controller.signal);
        if (controller.signal.aborted) return;
        setState(result.status);
        setError('');
        if (result.status === 'completed') {
          api.reset();
          const session = await endpoints.session();
          if (controller.signal.aborted) return;
          (window as unknown as { BotServerMe: typeof session.me }).BotServerMe = session.me;
          applyRuntimePreferences(await endpoints.account.settings());
          ['steos:export:key', 'steos:export:operation', 'steos:person:pending'].forEach(key => sessionStorage.removeItem(key));
          sessionStorage.removeItem(storage);
          setIntent(null);
          await callback.current();
        } else timer = window.setTimeout(() => void poll(), 3000);
      } catch (failure) {
        if (controller.signal.aborted) return;
        if (failure instanceof ApiError && failure.status === 404) setState('not_found');
        else setError('Перенос ещё не подтверждён. Квитанция сохранена; проверка продолжится.');
        timer = window.setTimeout(() => void poll(), 5000);
      }
    };
    void poll();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [intent]);
  function prepare() {
    const value = pending() ?? { operation_id: uuid(), receipt: uuid().replaceAll('-', '') + uuid().replaceAll('-', '') };
    sessionStorage.setItem(storage, JSON.stringify(value));
    setIntent(value);
    return value;
  }
  function reject() {
    sessionStorage.removeItem(storage);
    setIntent(null);
    setState('');
  }
  return { intent, state, error, prepare, reject };
}
